import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { useMovieStore, useUIStore } from '../../stores';
import './MovieSelector.css';
import { PlusIcon, Cross1Icon, ChevronRightIcon, TrashIcon, ExitIcon, DotsHorizontalIcon, Pencil1Icon, } from '@radix-ui/react-icons';
import { FilmSlate } from '@phosphor-icons/react';
import { Button, DropdownMenu } from '@radix-ui/themes';
import * as Collapsible from '@radix-ui/react-collapsible';
import { usePost, usePatch, useOnClickOutside, useDelete } from '../../hooks';
import { useTranslation } from '../../i18n';
import { ConfirmModal } from '../ui/ConfirmModal';
const normalizeDimension = (value) => (value > 0 ? value : 500);
const getMovieSummaryId = (movie) => movie.movieId ?? movie.id;
const mergeMovieSummaries = (...candidates) => {
    const items = candidates.filter((item) => !!item);
    if (items.length === 0)
        return null;
    const merged = { ...items[0] };
    for (const item of items) {
        if (item.name?.trim())
            merged.name = item.name;
        if (item.description?.trim())
            merged.description = item.description;
        if (item.width && item.width > 0)
            merged.width = item.width;
        if (item.height && item.height > 0)
            merged.height = item.height;
        if (item.fps && item.fps > 0)
            merged.fps = item.fps;
        if (item.sceneCount != null)
            merged.sceneCount = item.sceneCount;
        if (item.sceneObjectCount != null)
            merged.sceneObjectCount = item.sceneObjectCount;
        if (item.layerCount != null)
            merged.layerCount = item.layerCount;
        if (item.movieId != null)
            merged.movieId = item.movieId;
        if (item.isSample)
            merged.isSample = true;
        if (item.lastOpenedAt)
            merged.lastOpenedAt = item.lastOpenedAt;
    }
    return merged;
};
const isSamePreviewSummary = (a, b) => a.id === b.id &&
    (a.movieId ?? a.id) === (b.movieId ?? b.id) &&
    (a.name ?? '') === (b.name ?? '') &&
    (a.description ?? '') === (b.description ?? '') &&
    (a.width ?? 0) === (b.width ?? 0) &&
    (a.height ?? 0) === (b.height ?? 0) &&
    (a.fps ?? 0) === (b.fps ?? 0) &&
    (a.sceneCount ?? 0) === (b.sceneCount ?? 0) &&
    (a.sceneObjectCount ?? 0) === (b.sceneObjectCount ?? 0) &&
    (a.layerCount ?? 0) === (b.layerCount ?? 0) &&
    !!a.isSample === !!b.isSample;
export function MovieSelector({ isOpen, onClose }) {
    const { t } = useTranslation();
    const [newMovieName, setNewMovieName] = useState('');
    const [newMovieDescription, setNewMovieDescription] = useState('');
    const [editedMovieName, setEditedMovieName] = useState('');
    const [editedMovieDescription, setEditedMovieDescription] = useState('');
    const [showNewMovieForm, setShowNewMovieForm] = useState(false);
    const [previewMovie, setPreviewMovie] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [saveError, setSaveError] = useState('');
    const { setWelcomeOpen } = useUIStore();
    const containerRef = useRef(null);
    const { currentMovie, updateMovie, deleteMovie, unloadMovie, userAllMovies, setUserAllMovies, userRecentMovies, setUserRecentMovies, setSelectedMovieId, } = useMovieStore();
    const previewMovieId = previewMovie ? getMovieSummaryId(previewMovie) : null;
    const currentMovieId = currentMovie?.id ? Number(currentMovie.id) : null;
    const isPreviewCurrentMovie = previewMovieId != null &&
        currentMovieId != null &&
        !Number.isNaN(currentMovieId) &&
        previewMovieId === currentMovieId;
    const { execute: createMovie } = usePost('/api/movies/create');
    const updateMovieUrl = previewMovieId ? `/api/movies/${previewMovieId}/update` : null;
    const deleteMovieUrl = previewMovieId ? `/api/movies/${previewMovieId}/delete` : null;
    const { execute: saveMovieToApi, error: saveMovieApiError } = usePatch(updateMovieUrl);
    const { execute: deleteMovieFromApi } = useDelete(deleteMovieUrl);
    const resolvePreviewMovie = (movie) => {
        const movieId = getMovieSummaryId(movie);
        const fromAll = userAllMovies.find((item) => getMovieSummaryId(item) === movieId);
        const fromRecent = userRecentMovies.find((item) => getMovieSummaryId(item) === movieId);
        return mergeMovieSummaries(movie, fromRecent, fromAll) ?? movie;
    };
    const applySummaryUpdates = (movieId, updates) => {
        const patchList = (list) => list.map((movie) => getMovieSummaryId(movie) === movieId ? { ...movie, ...updates } : movie);
        setUserAllMovies(patchList(userAllMovies));
        setUserRecentMovies(patchList(userRecentMovies));
        setPreviewMovie((current) => current && getMovieSummaryId(current) === movieId ? { ...current, ...updates } : current);
        if (currentMovieId === movieId) {
            updateMovie({
                name: updates.name ?? currentMovie?.name,
                description: updates.description ?? currentMovie?.description,
                width: updates.width ?? currentMovie?.width,
                height: updates.height ?? currentMovie?.height,
            });
        }
    };
    const handlePreviewMovie = (movie) => {
        const resolved = resolvePreviewMovie(movie);
        setShowNewMovieForm(false);
        setNewMovieName('');
        setNewMovieDescription('');
        setPreviewMovie(resolved);
        setIsEditing(false);
        setEditedMovieName(resolved.name ?? '');
        setEditedMovieDescription(resolved.description ?? '');
        setSaveError('');
    };
    const clearPreview = () => {
        setPreviewMovie(null);
        setIsEditing(false);
        setSaveError('');
    };
    useOnClickOutside(containerRef, (event) => {
        if (isDeleteConfirmOpen) {
            return;
        }
        // Radix menus/dialogs portal outside this container; ignore those clicks
        // so selecting a preview "..." action does not close the whole panel.
        const target = event.target;
        if (target instanceof Element &&
            target.closest('[data-radix-popper-content-wrapper], [data-radix-menu-content], [role="dialog"]')) {
            return;
        }
        if (isOpen) {
            onClose();
        }
    });
    useEffect(() => {
        if (!isOpen) {
            clearPreview();
            setShowNewMovieForm(false);
            return;
        }
        if (!currentMovie || !currentMovieId || Number.isNaN(currentMovieId)) {
            return;
        }
        const fromLists = userAllMovies.find((item) => getMovieSummaryId(item) === currentMovieId) ??
            userRecentMovies.find((item) => getMovieSummaryId(item) === currentMovieId);
        handlePreviewMovie(fromLists ?? {
            id: currentMovieId,
            name: currentMovie.name,
            description: currentMovie.description,
            width: currentMovie.width,
            height: currentMovie.height,
            fps: currentMovie.fps,
            isSample: false,
        });
        // Only seed preview when the selector opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    useEffect(() => {
        if (!previewMovie || isEditing)
            return;
        const resolved = resolvePreviewMovie(previewMovie);
        if (isSamePreviewSummary(previewMovie, resolved))
            return;
        setPreviewMovie(resolved);
        setEditedMovieName(resolved.name ?? '');
        setEditedMovieDescription(resolved.description ?? '');
    }, [previewMovie, isEditing, userAllMovies, userRecentMovies]);
    if (!isOpen) {
        return null;
    }
    const handleCreateNewMovie = async () => {
        if (!newMovieName.trim())
            return;
        const newMovie = await createMovie({ name: newMovieName });
        if (newMovie) {
            const movieSummary = {
                id: newMovie.id,
                name: newMovie.name,
                description: newMovieDescription.trim(),
                width: normalizeDimension(newMovie.width),
                height: normalizeDimension(newMovie.height),
                fps: newMovie.fps,
                sceneCount: newMovie.sceneCount ?? 0,
                sceneObjectCount: newMovie.sceneObjectCount ?? 0,
                layerCount: newMovie.layerCount ?? 0,
                isSample: false,
            };
            setUserAllMovies([...userAllMovies, movieSummary]);
            setSelectedMovieId(newMovie.id);
            onClose();
        }
        setShowNewMovieForm(false);
        setNewMovieName('');
        setNewMovieDescription('');
    };
    const handleStartEditing = () => {
        if (!previewMovie)
            return;
        setEditedMovieName(previewMovie.name ?? '');
        setEditedMovieDescription(previewMovie.description ?? '');
        setSaveError('');
        setIsEditing(true);
    };
    const handleCancelEditing = () => {
        setIsEditing(false);
        setSaveError('');
        setEditedMovieName(previewMovie?.name ?? '');
        setEditedMovieDescription(previewMovie?.description ?? '');
    };
    const handleSavePreviewMovie = async () => {
        if (!previewMovie || previewMovieId === null) {
            setSaveError(t('movieSelector.errors.idUnresolved'));
            return;
        }
        const trimmedName = editedMovieName.trim();
        if (!trimmedName) {
            setSaveError(t('movieSelector.errors.nameEmpty'));
            return;
        }
        setSaveError('');
        const safeWidth = normalizeDimension(previewMovie.width ?? 0);
        const safeHeight = normalizeDimension(previewMovie.height ?? 0);
        const safeFps = previewMovie.fps && previewMovie.fps > 0 ? previewMovie.fps : 30;
        const trimmedDescription = editedMovieDescription.trim();
        try {
            const response = await saveMovieToApi({
                name: trimmedName,
                description: trimmedDescription,
                width: safeWidth,
                height: safeHeight,
                fps: safeFps,
            });
            if (response) {
                applySummaryUpdates(previewMovieId, {
                    name: trimmedName,
                    description: trimmedDescription,
                    width: safeWidth,
                    height: safeHeight,
                    fps: safeFps,
                });
                setIsEditing(false);
                return;
            }
            setSaveError(saveMovieApiError || t('movieSelector.errors.saveFailed'));
        }
        catch (error) {
            console.error('Failed to save movie:', error);
            setSaveError(saveMovieApiError ||
                (error instanceof Error ? error.message : t('movieSelector.errors.saveFailed')));
        }
    };
    const getRecentMovieDate = (movieSummary) => {
        const candidateDate = movieSummary.lastOpenedAt ?? movieSummary.updatedAt ?? movieSummary.createdAt;
        if (!candidateDate)
            return null;
        const parsed = new Date(candidateDate);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };
    const getDaysAgo = (targetDate) => {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const targetStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
        const diffMs = todayStart.getTime() - targetStart.getTime();
        return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    };
    const getRecentSectionLabel = (daysAgo) => {
        if (daysAgo <= 0)
            return t('movieSelector.today');
        if (daysAgo === 1)
            return t('movieSelector.yesterday');
        return t('movieSelector.daysAgo', { count: daysAgo });
    };
    const groupedRecentMovies = userRecentMovies.reduce((groups, movieSummary) => {
        const movieDate = getRecentMovieDate(movieSummary);
        const daysAgo = movieDate ? getDaysAgo(movieDate) : 999;
        const sectionLabel = movieDate
            ? getRecentSectionLabel(daysAgo)
            : t('movieSelector.older');
        if (!groups[sectionLabel]) {
            groups[sectionLabel] = [];
        }
        groups[sectionLabel].push(movieSummary);
        return groups;
    }, {});
    const sortedRecentSectionLabels = Object.keys(groupedRecentMovies).sort((a, b) => {
        const first = groupedRecentMovies[a][0];
        const second = groupedRecentMovies[b][0];
        const firstDate = first ? getRecentMovieDate(first) : null;
        const secondDate = second ? getRecentMovieDate(second) : null;
        const firstDaysAgo = firstDate ? getDaysAgo(firstDate) : Number.MAX_SAFE_INTEGER;
        const secondDaysAgo = secondDate ? getDaysAgo(secondDate) : Number.MAX_SAFE_INTEGER;
        return firstDaysAgo - secondDaysAgo;
    });
    const handleDeleteMovie = async () => {
        if (!previewMovie || previewMovieId === null) {
            setSaveError(t('movieSelector.errors.idUnresolved'));
            return;
        }
        await deleteMovieFromApi();
        deleteMovie(previewMovieId);
        if (isPreviewCurrentMovie) {
            unloadMovie();
            setWelcomeOpen(true);
            setIsDeleteConfirmOpen(false);
            onClose();
            return;
        }
        clearPreview();
        setIsDeleteConfirmOpen(false);
    };
    const handleUnloadMovie = () => {
        unloadMovie();
        setWelcomeOpen(true);
        setIsEditing(false);
        onClose();
    };
    const handleOpenPreviewedMovie = () => {
        if (!previewMovie || previewMovieId === null)
            return;
        setSelectedMovieId(previewMovieId);
        onClose();
    };
    const width = previewMovie?.width && previewMovie.width > 0 ? previewMovie.width : null;
    const height = previewMovie?.height && previewMovie.height > 0 ? previewMovie.height : null;
    const fps = previewMovie?.fps && previewMovie.fps > 0 ? previewMovie.fps : null;
    const renderMovieListItem = (movieSummary) => {
        const movieId = getMovieSummaryId(movieSummary);
        const isActive = previewMovieId === movieId;
        return (_jsxs("li", { className: `movie-list-item${isActive ? ' is-active' : ''}`, onClick: () => handlePreviewMovie(movieSummary), children: [_jsx(FilmSlate, { className: "movie-list-icon", "aria-hidden": "true" }), _jsx("span", { className: "movie-list-name", children: movieSummary.name })] }, movieSummary.id));
    };
    return (_jsx("div", { className: "movie-selection-area relative z-50", ref: containerRef, children: _jsxs("div", { className: "movie-selection-modal absolute top-0 left-0 z-50", children: [_jsxs("div", { className: "movie-selection-lists", children: [_jsxs("div", { className: "movie-create-area", children: [!showNewMovieForm && (_jsxs(Button, { variant: "soft", size: "2", className: "movie-create-button", onClick: () => {
                                        clearPreview();
                                        setShowNewMovieForm(true);
                                    }, children: [_jsx(PlusIcon, { "aria-hidden": "true" }), t('movieSelector.newMovie')] })), showNewMovieForm && (_jsxs("div", { className: "create-movie-form", children: [_jsx("div", { className: "create-movie-form-header", children: _jsx("span", { className: "create-movie-form-label", children: t('movieSelector.createNewMovie') }) }), _jsx("input", { type: "text", className: "input", placeholder: t('movieSelector.movieNamePlaceholder'), value: newMovieName, onChange: (e) => setNewMovieName(e.target.value), onKeyDown: (e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    void handleCreateNewMovie();
                                                }
                                                else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    setShowNewMovieForm(false);
                                                    setNewMovieName('');
                                                    setNewMovieDescription('');
                                                }
                                            }, autoFocus: true }), _jsx("textarea", { className: "input", rows: 3, placeholder: t('movieSelector.movieDescriptionPlaceholder'), value: newMovieDescription, onChange: (e) => setNewMovieDescription(e.target.value) }), _jsxs("div", { className: "create-movie-form-actions", children: [_jsxs(Button, { variant: "soft", size: "2", disabled: !newMovieName.trim(), onClick: () => void handleCreateNewMovie(), children: [_jsx(PlusIcon, { "aria-hidden": "true" }), t('common.create')] }), _jsx(Button, { variant: "surface", size: "2", color: "gray", onClick: () => {
                                                        setShowNewMovieForm(false);
                                                        setNewMovieName('');
                                                        setNewMovieDescription('');
                                                    }, children: t('common.cancel') })] })] }))] }), _jsxs(Collapsible.Root, { defaultOpen: true, children: [_jsxs(Collapsible.Trigger, { className: "collapsible-trigger", children: [_jsx("span", { className: "collapsible-arrow", children: _jsx(ChevronRightIcon, {}) }), _jsx("label", { className: "text-md font-light text-theme-primary", children: t('movieSelector.recent') })] }), _jsx(Collapsible.Content, { className: "collapsible-content", children: _jsx("ul", { className: "movie-list", children: sortedRecentSectionLabels.map((sectionLabel) => (_jsxs("li", { className: "movie-list-group", children: [_jsx("div", { className: "movie-list-group-title", children: sectionLabel }), _jsx("ul", { className: "movie-list", children: groupedRecentMovies[sectionLabel].map(renderMovieListItem) })] }, sectionLabel))) }) })] }), _jsxs(Collapsible.Root, { defaultOpen: true, children: [_jsxs(Collapsible.Trigger, { className: "collapsible-trigger", children: [_jsx("span", { className: "collapsible-arrow", children: _jsx(ChevronRightIcon, {}) }), _jsx("label", { className: "text-md font-light text-theme-primary", children: t('movieSelector.myProjects') })] }), _jsx(Collapsible.Content, { className: "collapsible-content", children: _jsx("ul", { className: "movie-list", children: userAllMovies.map(renderMovieListItem) }) })] })] }), _jsx("div", { className: "movie-selection-preview", children: previewMovie ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "movie-selection-preview-header", children: [_jsx("span", { className: "movie-selection-preview-title", children: isPreviewCurrentMovie
                                            ? t('movieSelector.currentMovie')
                                            : t('welcome.previewTitle') }), _jsxs("div", { className: "movie-selection-preview-header-actions", children: [_jsxs(DropdownMenu.Root, { children: [_jsx(DropdownMenu.Trigger, { children: _jsx(Button, { variant: "ghost", size: "1", className: "movie-selection-more-button", "aria-label": t('movieSelector.moreActions'), children: _jsx(DotsHorizontalIcon, { "aria-hidden": "true" }) }) }), _jsxs(DropdownMenu.Content, { variant: "soft", size: "2", align: "end", sideOffset: 4, children: [_jsxs(DropdownMenu.Item, { onSelect: handleStartEditing, children: [_jsx(Pencil1Icon, { "aria-hidden": "true" }), t('common.edit')] }), isPreviewCurrentMovie && (_jsxs(DropdownMenu.Item, { onSelect: handleUnloadMovie, children: [_jsx(ExitIcon, { "aria-hidden": "true" }), t('movieSelector.closeMovie')] })), _jsxs(DropdownMenu.Item, { color: "red", onSelect: () => setIsDeleteConfirmOpen(true), children: [_jsx(TrashIcon, { "aria-hidden": "true" }), t('movieSelector.deleteMovie')] })] })] }), _jsx(Button, { variant: "ghost", size: "1", className: "movie-selection-preview-close", onClick: onClose, "aria-label": t('common.close'), children: _jsx(Cross1Icon, { "aria-hidden": "true" }) })] })] }), _jsxs("div", { className: "movie-selection-preview-body", children: [_jsxs("div", { className: "movie-selection-preview-identity", children: [_jsx(FilmSlate, { className: "movie-selection-icon", "aria-hidden": "true" }), _jsx("div", { className: "movie-selection-preview-text", children: isEditing ? (_jsxs("div", { className: "movie-selection-preview-edit-fields", children: [_jsx("input", { type: "text", className: "input", value: editedMovieName, onChange: (e) => setEditedMovieName(e.target.value), autoFocus: true }), _jsx("textarea", { value: editedMovieDescription, placeholder: t('movieSelector.movieDescriptionPlaceholder'), rows: 3, className: "input", onChange: (e) => setEditedMovieDescription(e.target.value) })] })) : (_jsxs(_Fragment, { children: [_jsx("h3", { className: "movie-selection-preview-name", children: previewMovie.name }), _jsx("p", { className: "movie-selection-preview-description", children: previewMovie.description?.trim()
                                                                ? previewMovie.description
                                                                : t('welcome.noDescription') })] })) })] }), saveError && _jsx("div", { className: "movie-selection-save-error", children: saveError }), _jsxs("dl", { className: "movie-selection-preview-meta", children: [(width !== null || height !== null) && (_jsxs("div", { className: "movie-selection-preview-meta-row", children: [_jsx("dt", { children: t('welcome.resolution') }), _jsxs("dd", { children: [width ?? '—', " \u00D7 ", height ?? '—'] })] })), fps !== null && (_jsxs("div", { className: "movie-selection-preview-meta-row", children: [_jsx("dt", { children: t('welcome.fps') }), _jsx("dd", { children: t('welcome.fpsValue', { fps }) })] })), _jsxs("div", { className: "movie-selection-preview-meta-row", children: [_jsx("dt", { children: t('welcome.scenes') }), _jsx("dd", { children: previewMovie.sceneCount ?? 0 })] }), _jsxs("div", { className: "movie-selection-preview-meta-row", children: [_jsx("dt", { children: t('welcome.sceneObjects') }), _jsx("dd", { children: previewMovie.sceneObjectCount ?? 0 })] }), _jsxs("div", { className: "movie-selection-preview-meta-row", children: [_jsx("dt", { children: t('welcome.layers') }), _jsx("dd", { children: previewMovie.layerCount ?? 0 })] })] })] }), _jsx("div", { className: "movie-selection-preview-actions", children: isEditing ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "soft", size: "3", onClick: () => void handleSavePreviewMovie(), children: t('common.save') }), _jsx(Button, { variant: "surface", size: "3", color: "gray", onClick: handleCancelEditing, children: t('common.cancel') })] })) : (!isPreviewCurrentMovie && (_jsx(Button, { variant: "solid", size: "3", onClick: handleOpenPreviewedMovie, children: t('welcome.openProject') }))) })] })) : (_jsx("div", { className: "movie-selection-placeholder", children: t('movieSelector.selectToPreview') })) }), _jsx(ConfirmModal, { open: isDeleteConfirmOpen, onOpenChange: setIsDeleteConfirmOpen, title: t('movieSelector.deleteConfirmTitle'), description: t('movieSelector.deleteConfirmDescription', {
                        name: previewMovie?.name ?? t('movieSelector.thisMovie'),
                    }), confirmText: t('common.delete'), cancelText: t('common.cancel'), onConfirm: () => void handleDeleteMovie() })] }) }));
}
